// Ordering math for drag and drop. Containers keep a numeric `position`; moving one between two
// siblings gives it the midpoint, so a reorder is a single update of the moved item.

/** A position strictly between `before` and `after` (either may be missing at the ends). */
export function midpoint(before: number | undefined, after: number | undefined): number {
  if (before === undefined && after === undefined) return 1;
  if (before === undefined) return after! - 1;
  if (after === undefined) return before + 1;
  return before + (after - before) / 2;
}

/**
 * Position for `movedId` dropped at `index` in `siblings` (sorted by position; may include the moved
 * item itself, which is ignored). `index` counts the list without the moved item.
 */
export function positionAt<T extends { id: string; position: number }>(siblings: T[], movedId: string, index: number): number {
  const rest = siblings.filter((s) => s.id !== movedId);
  const i = Math.max(0, Math.min(rest.length, index));
  return midpoint(rest[i - 1]?.position, rest[i]?.position);
}

/**
 * Index the moved item should land at, in the list without it, when dropped on `overId`. Moving down
 * past the target lands after it; moving up lands before it (the usual sortable behavior).
 */
export function dropIndex(ids: string[], activeId: string, overId: string): number {
  const from = ids.indexOf(activeId);
  const rest = ids.filter((id) => id !== activeId);
  const over = rest.indexOf(overId);
  if (over === -1) return rest.length;
  if (from === -1) return over;
  return from <= ids.indexOf(overId) ? over + 1 : over;
}

/**
 * Apply a new order for a visible subset to the full order, keeping hidden items in their slots.
 * Used when a list shows only part of an ordered set (Today's focus filter, groups).
 */
export function mergeSubsetOrder(full: string[], reorderedSubset: string[]): string[] {
  const subset = new Set(reorderedSubset);
  const queue = [...reorderedSubset];
  const out = full.map((id) => (subset.has(id) ? queue.shift()! : id));
  // Items new to the full order (shouldn't happen) go at the end rather than being dropped.
  return [...out, ...queue];
}

/** Move `id` to `index` within `ids` (index counts the list without it). */
export function moveTo(ids: string[], id: string, index: number): string[] {
  const rest = ids.filter((x) => x !== id);
  rest.splice(Math.max(0, Math.min(rest.length, index)), 0, id);
  return rest;
}
